import React, { useState, useEffect, useRef } from "react";
import {
  FaSearch,
  FaThLarge,
  FaList,
  FaPlusCircle,
  FaSpinner,
} from "react-icons/fa";
import PrescriptionCard from "./PrescriptionCard";
import PrescriptionRow from "./PrescriptionRow";
import { useNavigate } from "react-router-dom";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";

const PrescriptionDashboard = ({ pharmacy }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("card");
  const [activeTab, setActiveTab] = useState("pending");
  const [prescriptions, setPrescriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const perPageLimit = 25; // Use 25 records per page in production

  const navigate = useNavigate();

  // Debounce the search query with a 500ms delay.
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Reset pagination when activeTab, pharmacy, or debounced search query changes.
  useEffect(() => {
    setCurrentPage(1);
    setPrescriptions([]);
    setHasMore(true);
  }, [activeTab, pharmacy, debouncedSearchQuery]);

  // Build query strings with pagination parameters and debounced search parameter.
  const pendingQuery = `?action=getPharmacyBill&pharmacy_id=${pharmacy}&status=draft&records_number=${perPageLimit}&page=${currentPage}&search=${encodeURIComponent(
    debouncedSearchQuery
  )}`;
  const completedQuery = `?action=getPharmacyBill&pharmacy_id=${pharmacy}&status=published&records_number=${perPageLimit}&page=${currentPage}&search=${encodeURIComponent(
    debouncedSearchQuery
  )}`;

  // Fetch data for pending or completed orders based on activeTab.
  const {
    data: pendingOrders,
    isLoading: isLoadingPendingOrders,
    error: pendingError,
  } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    pendingQuery,
    activeTab === "pending"
  );

  const {
    data: completedOrders,
    isLoading: isLoadingCompletedOrders,
    error: completedError,
  } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    completedQuery,
    activeTab === "completed"
  );

  // Stop infinite scrolling if a 404 error is returned.
  useEffect(() => {
    if (activeTab === "pending" && pendingError?.response?.status === 404) {
      console.log("No more data for pending orders. API returned 404.");
      setHasMore(false);
    }
    if (activeTab === "completed" && completedError?.response?.status === 404) {
      console.log("No more data for completed orders. API returned 404.");
      setHasMore(false);
    }
  }, [activeTab, pendingError, completedError]);

  // Append fetched data and update hasMore flag.
  useEffect(() => {
    if (activeTab === "pending" && pendingOrders) {
      if (currentPage === 1) {
        setPrescriptions(pendingOrders);
      } else {
        setPrescriptions((prev) => [...prev, ...pendingOrders]);
      }
      if (pendingOrders.length < perPageLimit) {
        setHasMore(false);
      }
    } else if (activeTab === "completed" && completedOrders) {
      if (currentPage === 1) {
        setPrescriptions(completedOrders);
      } else {
        setPrescriptions((prev) => [...prev, ...completedOrders]);
      }
      if (completedOrders.length < perPageLimit) {
        setHasMore(false);
      }
    }
  }, [activeTab, pendingOrders, completedOrders, currentPage, perPageLimit]);

  // In this version, the API returns already filtered data based on debouncedSearchQuery.
  const filteredPrescriptions = prescriptions;

  // Infinite scrolling: observe sentinel element.
  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return; // Stop if no more data available

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          ((activeTab === "pending" && !isLoadingPendingOrders) ||
            (activeTab === "completed" && !isLoadingCompletedOrders))
        ) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [activeTab, hasMore, isLoadingPendingOrders, isLoadingCompletedOrders]);

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Prescription Dashboard
        </h2>
        <div className="flex flex-wrap items-center space-x-2 sm:space-x-4">
          <button
            className={`flex items-center px-3 py-2 rounded-lg shadow text-sm sm:text-base ${
              viewMode === "card"
                ? "bg-green-600 text-white"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
            onClick={() => setViewMode("card")}
          >
            <FaThLarge className="mr-1" />
          </button>
          <button
            className={`flex items-center px-3 py-2 rounded-lg shadow text-sm sm:text-base ${
              viewMode === "list"
                ? "bg-green-600 text-white"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
            onClick={() => setViewMode("list")}
          >
            <FaList className="mr-1" />
          </button>
          <button
            className="flex items-center px-3 py-2 rounded-lg shadow bg-blue-600 text-white hover:bg-blue-700 transition"
            onClick={() =>
              navigate(`/new/pharmacy/bill/create/new?pharmacy_id=${pharmacy}`)
            }
          >
            <FaPlusCircle className="mr-1" /> Create Bill
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6">
        {[
          { id: "pending", label: "Pending Orders" },
          { id: "completed", label: "Completed Orders" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium flex-1 sm:flex-none ${
              activeTab === tab.id
                ? "border-b-2 border-green-600 text-green-600"
                : "text-gray-600 hover:text-green-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="flex items-center space-x-2 mb-6">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded shadow-sm focus:ring-green-300 focus:border-green-400"
          placeholder="Search by Mobile Number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="bg-green-600 text-white px-4 py-2 rounded-lg shadow hover:bg-green-700">
          <FaSearch />
        </button>
      </div>

      {/* Loading Message at Top (if initial load) */}
      {(isLoadingPendingOrders || isLoadingCompletedOrders) &&
        currentPage === 1 && (
          <p className="text-center text-green-600 font-semibold mb-4">
            Loading prescriptions...
          </p>
        )}

      {/* View Mode Rendering */}
      {viewMode === "card" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrescriptions.length > 0
            ? filteredPrescriptions.map((prescription) => (
                <PrescriptionCard
                  key={prescription.id}
                  prescription={prescription}
                />
              ))
            : !isLoadingPendingOrders &&
              !isLoadingCompletedOrders && (
                <p className="text-center col-span-full text-gray-500">
                  No prescriptions found.
                </p>
              )}
          {/* Sentinel element for infinite scrolling */}
          <div ref={loadMoreRef} className="h-8"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="table-auto w-full text-left border-collapse">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-4 py-2">Prescription No</th>
                <th className="px-4 py-2">Patient Name</th>
                <th className="px-4 py-2">Age</th>
                <th className="px-4 py-2">Mobile</th>
                <th className="px-4 py-2">Gender</th>
                <th className="px-4 py-2">Department</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPrescriptions.length > 0
                ? filteredPrescriptions.map((prescription) => (
                    <PrescriptionRow
                      key={prescription.id}
                      prescription={prescription}
                    />
                  ))
                : !isLoadingPendingOrders &&
                  !isLoadingCompletedOrders && (
                    <tr>
                      <td
                        colSpan="10"
                        className="px-4 py-2 text-center text-gray-500"
                      >
                        No prescriptions found.
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
          {/* Sentinel element for infinite scrolling */}
          <div ref={loadMoreRef} className="h-8"></div>
        </div>
      )}

      {/* Loader at Bottom if loading more data */}
      {(isLoadingPendingOrders || isLoadingCompletedOrders) &&
        currentPage > 1 && (
          <div className="flex justify-center items-center mt-4">
            <FaSpinner className="animate-spin text-green-600 text-4xl" />
          </div>
        )}
    </div>
  );
};

export default PrescriptionDashboard;
